// External Imports
import { useContext, useState, useEffect } from "react";
import { MdClose } from "react-icons/md";

// Internal Imports
import { requestAdminAPI } from "../../api/posh-api";
import { UtilityContext } from "../../context/util-context";
import { constructDateObject } from "../../util/format-date";
import Heading from "../UI/Heading";
import uploadFile from "../../util/upload-file";
import Modal from "../UI/Modal";
import VehicleSelector from "../vehicles-page/VehicleSelector";
import DetailPair from "../UI/DetailPair";
import LinkPair from "../UI/LinkPair";
import LoadingSpinnerWrapper from "../UI/LoadingSpinnerWrapper";
import SelectPair from "../UI/SelectPair";
import EmployeeSelector from "../employees-page/EmployeeSelector";
import LocationSelector from "../locations-page/LocationSelector";
import ApplicationSelector from "../applications-page/ApplicationSelector";
import { format } from "date-fns";
import PartnerSelector from "../partner-page/PartnerSelector";
import SupplierSelector from "../UI/SupplierSelector";
import { validateDocumentFile } from "../../util/validate-file";
import ExpenseOptions from "./expense-options.json";

/** ========================================================================
 * * Add Expense Modal
 * Renders a Modal, where the user can add a new expense.
 * ========================================================================== */
export default function AddExpense({
  open,
  setOpen,
  onComplete,
  pending: defaultPending = false,
  defaultCategory = "location-rent-69100",
  defaultSelected,
  defaultDescription = "",
  defaultPaidVia = "_cash",
  defaultDate,
  defaultAmount,
  association,
}) {
  const { navigate } = useContext(UtilityContext);
  // Initialize States
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState(defaultCategory);
  const [record, setRecord] = useState(defaultSelected);
  const [pending, setPending] = useState(defaultPending);
  const [dateError, setDateError] = useState("");
  const [error, setError] = useState("");

  /* ======================= UPDATE DATE INPUT MIN ATTRIBUTE ======================= */
  useEffect(() => {
    const dateInput = document.getElementById("date");
    if (dateInput) {
      if (pending) {
        dateInput.setAttribute("min", format(new Date(), "yyyy-MM-dd"));
      } else {
        dateInput.removeAttribute("min");
      }
    }
  }, [pending]);

  /* ======================= VALIDATE DATE ======================= */
  const validateDate = (dateValue) => {
    if (!pending) {
      setDateError("");
      return true;
    }

    if (!dateValue) {
      setDateError("");
      return true; // Let required attribute handle empty validation
    }

    const selectedDate = new Date(dateValue);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      setDateError("Due Date cannot be in the past for pending expenses");
      return false;
    }

    setDateError("");
    return true;
  };

  /* ======================= ADD EXPENSE ======================= */
  const submitHandler = async () => {
    try {
      // Validate date before submission
      const dateInput = document.getElementById("date");
      if (!validateDate(dateInput?.value)) {
        return; // Prevent submission if validation fails
      }

      setLoading(true);
      
      // Validate file format if a file is selected
      const file = document.getElementById("attachment")?.files[0];
      if (file) {
        const validation = validateDocumentFile(file);
        if (!validation.isValid) {
          setError(validation.error);
          setLoading(false);
          return;
        }
      }
      
      // Create Payload for request
      const payload = {
        amount: document.getElementById("amount").value,
        status: pending ? "pending" : "succeeded",
        category: category,
        description: document.getElementById("description").value,
        supplierId: document.getElementById("supplier")?.value,
        ...(association ? association : {}),
      };

      // Set paid/due date
      const selectedDate = constructDateObject(
        document.getElementById("date")?.value,
        "15:00:00",
      ).toISOString();
      if (pending) payload.dueDate = selectedDate;
      else {
        payload.datePaid = selectedDate;
        payload.paidVia = document.getElementById("paidVia")?.value;
      }

      // Set Category
      if (payload.category === "other")
        payload.category = document
          .getElementById("other")
          ?.value?.split(" ")[0];
      else {
        const table = category.split("-")[0];
        payload[`${table}Id`] = record;
      }

      // Upload Attachment/Receipt
      if (file) {
        const path = `expenses/${
          record ? record : payload.category
        }/${new Date().getTime()}`;
        const url = await uploadFile(path, file, () => {});
        pending ? (payload.attachment = url) : (payload.receipt = url);
      }

      // Send Request
      const expenseId = await requestAdminAPI("add-expense", payload);
      onComplete
        ? onComplete(expenseId)
        : navigate(`/expenses/info?id=${expenseId}`);
      setOpen(false);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setError(err.message || "An error occurred. Please try again.");
      setLoading(false);
    }
  };

  return (
    <Modal open={open} setOpen={setOpen}>
      <div className="modal lg:w-[40vw]">
        <Heading
          title={pending ? "Schedule Expense" : "Add Expense"}
          isBackArrow={false}
          text="Try to add expenses from designated information pages (such as application info page for vehicle installment expenses) because it will prefill most of the information."
        >
          <MdClose onClick={() => setOpen(false)} className="icon" />
        </Heading>
        <div className="lg:grid-cols-3 info">
          <SelectPair
            label="Status"
            options={[
              { name: "Paid", id: "succeeded" },
              { name: "Pending", id: "pending" },
            ]}
            value={pending ? "pending" : "succeeded"}
            inputOptions={{
              id: "type",
              onChange: (e) => {
                const newPending = e.target.value === "pending";
                setPending(newPending);
                // Clear date error when status changes
                setDateError("");
                // Re-validate date if switching to pending
                if (newPending) {
                  const dateInput = document.getElementById("date");
                  if (dateInput) {
                    validateDate(dateInput.value);
                  }
                }
              },
            }}
            edit={true}
          />
          <div>
            <DetailPair
              label={pending ? "Due Date" : "Date Paid"}
              edit={true}
              inputOptions={{
                id: "date",
                name: "date",
                type: "date",
                defaultValue: defaultDate || format(new Date(), "yyyy-MM-dd"),
                min: pending ? format(new Date(), "yyyy-MM-dd") : undefined,
                required: true,
                onChange: (e) => validateDate(e.target.value),
              }}
            />
            {dateError && (
              <p className="text-red-500 text-sm mt-1">{dateError}</p>
            )}
          </div>
          <DetailPair
            label="Amount"
            edit={true}
            inputOptions={{
              name: "amount",
              step: "0.01",
              type: "number",
              defaultValue: defaultAmount,
              required: true,
            }}
          />
          <SelectPair
            label="Category"
            options={ExpenseOptions.categories}
            edit={true}
            value={category}
            inputOptions={{
              id: "category",
              onChange: (e) => {
                setRecord();
                setCategory(e.target.value);
              },
              required: true,
            }}
          />
          <div className="self-center lg:col-span-2">
            {category.split("-")[0] === "vehicle" && (
              <VehicleSelector
                selected={record}
                onSelect={(r) => setRecord(r.id)}
              />
            )}
            {category.split("-")[0] === "employee" && (
              <EmployeeSelector
                selected={record}
                onSelect={(r) => setRecord(r.id)}
              />
            )}
            {category.split("-")[0] === "location" && (
              <LocationSelector
                selected={record}
                onSelect={(r) => setRecord(r.id)}
              />
            )}
            {category.split("-")[0] == "application" && (
              <ApplicationSelector
                selected={record}
                onSelect={(r) => setRecord(r.id)}
              />
            )}
            {category.split("-")[0] == "partner" && (
              <PartnerSelector
                selected={record}
                onSelect={(r) => setRecord(r.id)}
              />
            )}
            {category.split("-")[0] == "other" && (
              <DetailPair
                label="Specify category in one word"
                edit={true}
                inputOptions={{
                  name: "other",
                  type: "text",
                }}
              />
            )}
          </div>
          {!pending && (
            <SelectPair
              label="Paid Via"
              options={ExpenseOptions.paymentMethods}
              edit={true}
              inputOptions={{
                id: "paidVia",
                defaultValue: defaultPaidVia,
              }}
            />
          )}
          <div className="lg:col-span-2">
            <DetailPair
              label="Description"
              edit={true}
              inputOptions={{
                name: "description",
                type: "text",
                defaultValue: defaultDescription,
                required: true,
              }}
            />
          </div>
          <LinkPair
            label={pending ? "Attachment" : "Receipt"}
            edit={true}
            inputOptions={{
              name: "attachment",
              type: "file",
              accept: ".png,.webp,.pdf,.PNG,.WEBP,.PDF,image/png,image/webp,application/pdf",
            }}
          />
          {error && (
            <div className="lg:col-span-3 p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded">
              {error}
            </div>
          )}
          <div className="col-span-2">
            <SupplierSelector onSelect={() => {}} />
          </div>

          <div className="lg:col-span-3">
            {loading ? (
              <LoadingSpinnerWrapper />
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="btn-secondary" onClick={() => setOpen(false)}>
                  Cancel
                </div>
                <button onClick={submitHandler} className="btn-primary">
                  Submit
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
